import { useCallback, useEffect, useState } from 'react';

import { api } from '../api.ts';

/**
 * Levél kiküldése. (3.0 / 4.)
 *
 * Egy okmány elküldése a partnernek: a levél szövege a beállított kísérő
 * levélből jön, a csatolmány a bizonylat PDF-je. **Magától semmi nem megy
 * ki** — ez a panel mindig megmutatja, kinek megy, és csak a gombra
 * indul el.
 *
 * A címet itt is át lehet írni, ha a partnerhez más cím tartozik. Ez a
 * levélre vonatkozik, a partner adatlapját nem módosítja.
 */

export type LevelTipus = 'szallitolevel' | 'kiadas' | 'szamla' | 'felszolitas' | 'szerzodes';

export interface LevelKeres {
  tipus: LevelTipus;
  /** Mit küldünk — a panel fejlécében jelenik meg. */
  cim: string;
  /** Kinek: ezekből keressük ki a címet, ha nincs megadva. */
  etterem_id?: number | null;
  ceg_id?: number | null;
  /** Átvevő (a kiadási okmányoknál). */
  atvevo_id?: number | null;
  cimzett?: string | null;
  /** A naplóhoz és a megőrzött okmány megkereséséhez. */
  tabla?: string | null;
  rekord_id?: number | null;
  /** Amit a levél szövegébe behelyettesítünk. */
  adatok?: Record<string, string | number | null>;
  /** A kész dokumentum HTML-je, ha a felület rajzolja (felszólítás, szerződés). */
  html?: string | null;
  fajlnev?: string | null;
}

interface Eredmeny {
  sikeres: boolean;
  cimzett: string;
  eredeti_cimzett: string;
  targy: string;
  teszt_mod: boolean;
  archiv: string | null;
  hiba?: string;
}

export function LevelKuldoPanel({
  keres,
  onKesz,
  onMegse,
}: {
  keres: LevelKeres;
  /** A sikeres küldés után — az üzenetet a hívó képernyő mutatja. */
  onKesz: (uzenet: string) => void;
  onMegse: () => void;
}) {
  const [cim, setCim] = useState(keres.cimzett ?? '');
  const [toltes, setToltes] = useState(false);
  const [kuldes, setKuldes] = useState(false);
  const [hiba, setHiba] = useState<string | null>(null);
  /** A levél szövege, ahogy kimenne — a szerver adja, nem itt állítjuk össze. */
  const [elonezet, setElonezet] = useState<{ targy: string; torzs: string } | null>(null);

  const cimKeresese = useCallback(async () => {
    if (keres.cimzett) return;
    const mit = keres.etterem_id
      ? `etterem_id=${keres.etterem_id}`
      : keres.ceg_id
        ? `ceg_id=${keres.ceg_id}`
        : keres.atvevo_id
          ? `atvevo_id=${keres.atvevo_id}`
          : null;
    if (!mit) return;
    setToltes(true);
    try {
      /* A levél fajtájából derül ki, melyik cím-kategória kell. (3.0 / 5.) */
      const v = await api.get<{ cim: string | null }>(
        `/api/level/cimzett?${mit}&tipus=${keres.tipus}`,
      );
      setCim(v.cim ?? '');
    } catch {
      /* ha nem sikerül, a mező üres marad, és a felhasználó beírja */
    } finally {
      setToltes(false);
    }
  }, [keres.cimzett, keres.etterem_id, keres.ceg_id, keres.atvevo_id, keres.tipus]);

  useEffect(() => {
    void cimKeresese();
  }, [cimKeresese]);

  /* A levél szövege a sablonból — küldés előtt látni kell, mi megy ki. */
  useEffect(() => {
    let elavult = false;
    api
      .post<{ targy: string; torzs: string }>('/api/level/elonezet', {
        tipus: keres.tipus,
        cimzett: cim.trim() || null,
        adatok: keres.adatok ?? {},
      })
      .then((v) => {
        if (!elavult) setElonezet(v);
      })
      .catch(() => {
        /* ha nem jön, a panel a szöveg nélkül is használható */
      });
    return () => {
      elavult = true;
    };
    /* A címet szándékosan nem figyeljük: gépelés közben ne kérdezzen újra. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keres.tipus, keres.adatok]);

  async function kuld() {
    setKuldes(true);
    setHiba(null);
    try {
      const v = await api.post<{ eredmeny: Eredmeny }>('/api/level/kuldes', {
        tipus: keres.tipus,
        cimzett: cim.trim(),
        tabla: keres.tabla ?? null,
        rekord_id: keres.rekord_id ?? null,
        adatok: keres.adatok ?? {},
        html: keres.html ?? null,
        fajlnev: keres.fajlnev ?? null,
      });
      const e = v.eredmeny;
      if (!e.sikeres) {
        setHiba(e.hiba ?? 'A küldés nem sikerült.');
        return;
      }
      onKesz(
        e.teszt_mod
          ? `Teszt módban vagyunk: a levél ${e.cimzett} címre ment, nem ${e.eredeti_cimzett} részére.`
          : `Elküldve ide: ${e.cimzett}.${e.archiv ? ` Archív másolat: ${e.archiv}.` : ''}`,
      );
    } catch (h) {
      setHiba(h instanceof Error ? h.message : 'A küldés nem sikerült.');
    } finally {
      setKuldes(false);
    }
  }

  return (
    <div className="npform" style={{ marginTop: 12 }}>
      <h4>Küldés e-mailben — {keres.cim}</h4>
      <div className="c" style={{ marginBottom: 8 }}>
        A levél szövege a beállított kísérő levélből jön, a csatolmány a bizonylat PDF-je. A cím itt
        átírható; a partner adatlapját ez nem módosítja.
      </div>
      <input
        autoFocus
        value={cim}
        placeholder={toltes ? 'cím keresése…' : 'e-mail cím'}
        onChange={(e) => setCim(e.target.value)}
      />
      {!toltes && !cim && (
        <div className="c" style={{ marginTop: 6 }}>
          Ehhez a partnerhez nincs e-mail cím rögzítve — írd be ide, vagy vedd fel a Partnerek
          képernyőn.
        </div>
      )}
      {elonezet && (
        <div style={{ marginTop: 10 }}>
          <div className="c">
            <b>Tárgy:</b> {elonezet.targy}
          </div>
          <pre
            style={{
              margin: '4px 0 0',
              padding: '8px 10px',
              background: 'var(--paper)',
              border: '1px solid var(--rule)',
              font: 'inherit',
              whiteSpace: 'pre-wrap',
              maxHeight: 180,
              overflow: 'auto',
            }}
          >
            {elonezet.torzs}
          </pre>
          <div className="hint" style={{ marginTop: 4 }}>
            Ez megy ki, a bizonylat PDF-je csatolva. A szöveg a{' '}
            <b>Beállítások → E-mail és sablonok</b> képernyőn módosítható.
          </div>
        </div>
      )}
      {hiba && (
        <div className="uzenet bad" style={{ marginTop: 8 }}>
          {hiba}
        </div>
      )}
      <div className="btns">
        <button className="btn sm pri" disabled={!cim.includes('@') || kuldes} onClick={() => void kuld()}>
          {kuldes ? 'küldés…' : 'Küldés'}
        </button>
        <button className="btn sm" disabled={kuldes} onClick={onMegse}>
          Mégse
        </button>
      </div>
    </div>
  );
}
